import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import '../styles/Lead.css';
import AddLead from './AddLead';
import EmbedForm from './EmbedForm';
import Kanban from './Kanban';
import LeadDashboard from './LeadDashboard';
import LeadViewModal from './LeadViewModal';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  parseLeadSource,
  parseLeadPipelineLabel,
} from '../utils/leadSource';

const PAGE_SIZE = 10;

function getInitials(name) {
  return String(name || '?')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function leadScore(lead) {
  const status = String(lead.label || '').toLowerCase();
  if (status === 'cancel') return { score: 0, temp: 'Lost', tone: 'cold' };

  // Score only from information that helps the team qualify and progress the
  // lead. This avoids presenting a made-up score based solely on pipeline status.
  const signals = [
    [Boolean(lead.contactEmail), 15],
    [Boolean(lead.contactNumber), 15],
    [Boolean(lead.address), 10],
    [Boolean(lead.postcode), 5],
    [Boolean(lead.serviceType || lead.serviceName), 15],
    [Boolean(lead.message && String(lead.message).trim().length >= 20), 10],
    [Boolean(lead.preferredDate), 10],
    [Number(lead.estimatedValue) > 0, 15],
    [Boolean(lead.branchId), 5],
  ];
  const score = signals.reduce((total, [present, weight]) => total + (present ? weight : 0), 0);
  if (score >= 75) return { score, temp: 'Ready', tone: 'warm' };
  if (score >= 50) return { score, temp: 'Developing', tone: 'moderate' };
  return { score, temp: 'Needs info', tone: 'cold' };
}

function leadNextAction(lead) {
  const status = String(lead.label || 'pending').toLowerCase();
  if (status === 'cancel') return { label: 'Review loss reason', tone: 'muted' };
  if (!lead.contactEmail && !lead.contactNumber) {
    return { label: 'Add contact details', tone: 'urgent' };
  }
  if (status === 'pending') return { label: 'Contact and qualify', tone: 'urgent' };
  if (status === 'approve') {
    return lead.preferredDate
      ? { label: 'Confirm inspection', tone: 'active' }
      : { label: 'Schedule inspection', tone: 'active' };
  }
  if (status === 'assigned') return { label: 'Prepare or follow up quote', tone: 'active' };
  return { label: 'Set next step', tone: 'muted' };
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function sourceClass(source) {
  const s = String(source || '').toLowerCase();
  if (s.includes('google')) return 'google';
  if (s.includes('referral')) return 'referral';
  if (s.includes('linkedin')) return 'linkedin';
  if (s.includes('facebook')) return 'facebook';
  if (s.includes('cold')) return 'cold';
  return 'website';
}

function toGraphQLStatus(status) {
  const map = {
    pending: 'PENDING',
    approve: 'APPROVED',
    assigned: 'ASSIGNED',
    cancel: 'CANCELLED',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    ASSIGNED: 'ASSIGNED',
    CANCELLED: 'CANCELLED',
  };
  return map[status] || status;
}

function fromGraphQLStatus(status) {
  const map = {
    PENDING: 'pending',
    APPROVED: 'approve',
    ASSIGNED: 'assigned',
    CANCELLED: 'cancel',
  };
  return map[status] || String(status || 'pending').toLowerCase();
}

const Lead = ({ setApprovedCompanies }) => {
  const location = useLocation();
  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('search') || '').trim().toLowerCase();
  }, [location.search]);
  const [AddLeadActive, setAddLeadActive] = useState(false);
  const [embedActive, setEmbedActive] = useState(false);
  const [active, setActive] = useState('dashboard');
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const [leadTotal, setLeadTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewLead, setViewLead] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const companyName = Cookies.get('companyName');

  // Navbar search should show the lead list, not the dashboard tiles.
  useEffect(() => {
    if (searchQuery) setActive('list');
  }, [searchQuery]);

  const fetchLeads = React.useCallback(async (pageNum, options = {}) => {
    const { append = false, background = false } = options;
    if (append) setLoadingMore(true);
    else if (!background) setLoading(true);
    const limit = active === 'list' ? PAGE_SIZE : 200;
    const safePage = Math.max(1, Number(pageNum) || 1);

    const query = `
      query FindAllLeads($page: Float!, $limit: Float!) {
        findAllLeads(page: $page, limit: $limit, includeSignUrls: false) {
          total
          page
          limit
          totalPages
          data {
            id
            fullName
            email
            phone
            address
            postcode
            bookingFrequency
            uploads
            message
            status
            estimatedPRice
            preferredDate
            createdAt
            updatedAt
            branchId
            branch {
              id
              name
            }
            stage {
              id
              name
            }
            bookingServices {
              id
              service {
                id
                service
              }
            }
          }
        }
      }
    `;

      try {
        const data = await graphqlRequest(query, {
          page: safePage,
          limit,
        });

        const bookingData = data?.findAllLeads?.data || [];
        const formatted = bookingData.map((b) => {
          const primaryService = Array.isArray(b.bookingServices)
            ? b.bookingServices.find((bs) => bs?.service?.id)?.service
            : null;
          return {
          id: b.id,
          companyName: companyName || 'My Company',
          clientName: b.fullName,
          contactNumber: b.phone,
          contactEmail: b.email,
          address: b.address,
          postcode: b.postcode || '',
          label: fromGraphQLStatus(b.status),
          stage: b.stage,
          bookingFrequency: b.bookingFrequency,
          message: b.message,
          preferredDate: b.preferredDate || '',
          estimatedValue: Number(String(b.estimatedPRice || '').replace(/[^0-9.]/g, '')) || 0,
          source: parseLeadSource(b.bookingFrequency),
          createdAt: b.createdAt,
          updatedAt: b.updatedAt,
          serviceType: primaryService?.id || '',
          serviceName: primaryService?.service || '',
          branchId: b.branchId || '',
          branchName: b.branch?.name || '',
          uploads: Array.isArray(b.uploads) ? b.uploads : [],
          };
        });

        setLeads((previous) => {
          if (append) {
            const known = new Set(previous.map((lead) => lead.id));
            return [...previous, ...formatted.filter((lead) => !known.has(lead.id))];
          }
          if (background) {
            const incoming = new Map(formatted.map((lead) => [lead.id, lead]));
            const refreshed = previous.map((lead) => {
              if (!incoming.has(lead.id)) return lead;
              const next = incoming.get(lead.id);
              const merged = { ...lead, ...next };
              if (
                (!Array.isArray(next.uploads) || next.uploads.length === 0) &&
                Array.isArray(lead.uploads) &&
                lead.uploads.length > 0
              ) {
                merged.uploads = lead.uploads;
              }
              return merged;
            });
            const known = new Set(previous.map((lead) => lead.id));
            return [
              ...formatted.filter((lead) => !known.has(lead.id)),
              ...refreshed,
            ];
          }
          return formatted;
        });
        setLeadTotal(data?.findAllLeads?.total ?? formatted.length);
        const total = data?.findAllLeads?.total ?? formatted.length;
        const apiTotalPages = data?.findAllLeads?.totalPages;
        const computedPages = Math.max(1, Math.ceil(total / limit));
        setPage(data?.findAllLeads?.page ?? safePage);
        setTotalPages(
          Math.max(apiTotalPages ?? 0, computedPages) || 1,
        );
      } catch (err) {
        console.error('Error fetching leads:', err);
        toast.error(err.message || 'Failed to load leads');
      } finally {
        if (append) setLoadingMore(false);
        else if (!background) setLoading(false);
      }
  }, [companyName, active]);

  useEffect(() => {
    if (active === 'dashboard') return;
    fetchLeads(page, { append: active === 'list' && page > 1 });
  }, [fetchLeads, page, active]);

  useEffect(() => {
    setPage(1);
  }, [active]);

  useEffect(() => {
    if (active !== 'list' || loading || loadingMore || page >= totalPages) {
      return undefined;
    }
    const sentinel = loadMoreRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setPage((current) => Math.min(totalPages, current + 1));
        }
      },
      { rootMargin: '240px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [active, loading, loadingMore, page, totalPages]);

  // Keep lead views current without a full page reload. Public/embed/webhook
  // submissions are picked up by the visible-tab poll; in-app creates emit an event.
  useEffect(() => {
    if (active === 'dashboard' || active === 'kanban') return undefined;
    const refresh = () => fetchLeads(1, { background: true });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('lead:changed', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 30000);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('lead:changed', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [active, fetchLeads, page]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const mutation = `
        mutation UpdateBooking($id: String!, $status: taskStatus!) {
          updateBooking(updateBookingInput: { id: $id, status: $status }) {
            id
            status
            stage {
              id
              name
            }
          }
        }
      `;

      const data = await graphqlRequest(mutation, {
        id: leadId,
        status: toGraphQLStatus(newStatus),
      });
      const updated = data?.updateBooking;

      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId
            ? {
                ...l,
                label: newStatus,
                stage: updated?.stage || l.stage,
              }
            : l,
        ),
      );
    } catch (err) {
      console.error('Error updating lead status:', err);
      toast.error(err?.message || 'Failed to update lead status');
    }
  };

  const requestDeleteLead = (leadId) => {
    if (!leadId) return;
    setConfirmDeleteId(leadId);
  };

  const confirmDeleteLead = async () => {
    const leadId = confirmDeleteId;
    if (!leadId) return;
    setDeleting(true);
    try {
      const mutation = `
        mutation RemoveBooking($id: String!) {
          removeBooking(id: $id)
        }
      `;
      await graphqlRequest(mutation, { id: leadId });
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      if (selectedLead?.id === leadId) setSelectedLead(null);
      toast.success('Lead deleted');
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Error deleting lead:', err);
      toast.error(err.message || 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const effectiveTotalPages = Math.max(
    totalPages,
    Math.ceil(leadTotal / PAGE_SIZE) || 1,
  );
  const visibleLeads = useMemo(() => {
    const term = (listSearch || searchQuery).trim().toLowerCase();
    if (!term) return leads;
    return leads.filter((lead) =>
      [
        lead.clientName,
        lead.contactEmail,
        lead.contactNumber,
        lead.address,
        lead.message,
        lead.label,
        lead.stage?.name,
        lead.source,
      ].some((field) => String(field || '').toLowerCase().includes(term)),
    );
  }, [leads, searchQuery, listSearch]);

  useEffect(() => {
    const filtered = leads.filter((l) =>
      ['verified', 'closed won', 'approve'].some((s) =>
        l.label?.toLowerCase()?.includes(s),
      ),
    );
    setApprovedCompanies(filtered);
  }, [leads, setApprovedCompanies]);

  return (
    <div className="container">
      <div className="main">
        <div className="header-lead">
          <div>
            <h2 className="lead-page-title">Lead Management</h2>
            <p className="lead-page-description">
              Capture, qualify and convert enquiries into sales opportunities.
            </p>
          </div>
          <div className="tab-group">
            <button
              type="button"
              className={`tab-button ${active === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActive('dashboard')}
            >
              Dashboard
            </button>
            <button
              type="button"
              className={`tab-button ${active === 'list' ? 'active' : ''}`}
              onClick={() => {
                setActive('list');
                setPage(1);
              }}
            >
              List
            </button>
            <button
              type="button"
              className={`tab-button ${active === 'kanban' ? 'active' : ''}`}
              onClick={() => {
                setActive('kanban');
                setPage(1);
              }}
            >
              Kanban
            </button>
          </div>

          <div className="action-group" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="action-button primary"
              onClick={() => {
                setAddLeadActive(true);
                setEditMode(false);
              }}
            >
              + New lead
            </button>
            <button
              className="action-button"
              onClick={() => setEmbedActive(true)}
            >
              Lead capture & integrations
            </button>
          </div>
        </div>

        {!loading && searchQuery && (
          <p className="lead-summary">
            Filtered by &quot;{searchQuery}&quot; — {visibleLeads.length} match
            {visibleLeads.length === 1 ? '' : 'es'}
          </p>
        )}

        {!loading && leadTotal > 0 && !searchQuery && active !== 'dashboard' && (
          <p className="lead-summary">
            {active === 'list'
              ? `Showing ${leads.length} of ${leadTotal} leads · more leads load automatically as you scroll`
              : `Showing ${leads.length} of ${leadTotal} leads in Kanban`}
          </p>
        )}

        {active === 'dashboard' ? (
          <LeadDashboard />
        ) : loading ? (
          <p>Loading leads...</p>
        ) : active === 'list' ? (
          <>
          <div className="lead-list-toolbar">
            <input
              type="search"
              className="lead-list-search"
              placeholder="Search leads…"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
            />
            <span className="lead-list-count">
              Showing {visibleLeads.length} of {leadTotal} leads
            </span>
          </div>

          <div className="lead-table-wrap">
            <table className="lead-table">
              <thead>
                <tr>
                  <th className="lead-th-view" />
                  <th>Lead / Company</th>
                  <th>Contact</th>
                  <th>Source</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Value</th>
                  <th>Last activity</th>
                  <th>Next action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleLeads.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="lead-table-empty">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  visibleLeads.map((lead) => {
                    const { score, temp, tone } = leadScore(lead);
                    const industry =
                      lead.stage?.name ||
                      parseLeadPipelineLabel(lead.bookingFrequency) ||
                      'Lead';
                    return (
                      <tr key={lead.id} className="lead-table-row">
                        <td className="lead-td-view">
                          <button
                            type="button"
                            className="lead-view-btn"
                            onClick={() => setViewLead(lead)}
                          >
                            View
                          </button>
                        </td>
                        <td>
                          <div className="lead-company-cell">
                            <div className="lead-avatar">{getInitials(lead.clientName)}</div>
                            <div>
                              <strong className="lead-company-name">{lead.clientName}</strong>
                              <span className="lead-company-sub">{industry}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="lead-contact-cell">
                            <strong>{lead.clientName}</strong>
                            <span>{lead.contactEmail}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`lm-source-tag lm-source-tag--${sourceClass(lead.source)}`}>
                            {lead.source}
                          </span>
                        </td>
                        <td>
                          <div className="lead-score-cell">
                            <div
                              className={`lead-score-ring lead-score-ring--${tone}`}
                              style={{ '--score': score }}
                            >
                              <span>{score}</span>
                            </div>
                            <span className={`lead-score-temp lead-score-temp--${tone}`}>
                              {temp}
                            </span>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            className={`lm-status-badge lm-status-badge--select lm-status-badge--${lead.label || 'pending'}`}
                            value={lead.label || 'pending'}
                            onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          >
                            <option value="pending">New</option>
                            <option value="approve">Qualified</option>
                            <option value="assigned">In Progress</option>
                            <option value="cancel">Lost</option>
                          </select>
                        </td>
                        <td className="lead-value-cell">
                          {lead.estimatedValue
                            ? `$${lead.estimatedValue.toLocaleString()}/mo`
                            : '—'}
                        </td>
                        <td className="lead-meta-cell">
                          {formatRelativeDate(lead.updatedAt)}
                        </td>
                        <td>
                          {(() => {
                            const nextAction = leadNextAction(lead);
                            return (
                              <button
                                type="button"
                                className={`lead-next-action lead-next-action--${nextAction.tone}`}
                                onClick={() => setViewLead(lead)}
                              >
                                {nextAction.label}
                              </button>
                            );
                          })()}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="lead-row-actions">
                            <button
                              type="button"
                              className="lead-icon-btn"
                              title="Edit"
                              onClick={() => {
                                setSelectedLead(lead);
                                setEditMode(true);
                                setAddLeadActive(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="lead-icon-btn lead-icon-btn--danger"
                              title="Delete"
                              onClick={() => requestDeleteLead(lead.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div ref={loadMoreRef} className="lead-load-more" aria-live="polite">
              {loadingMore
                ? 'Loading more leads…'
                : page < effectiveTotalPages
                  ? 'Scroll to load more leads'
                  : `All ${leadTotal} leads loaded`}
            </div>
          </>
        ) : (
          <Kanban />
        )}

        {embedActive && <EmbedForm onClose={() => setEmbedActive(false)} />}

        {viewLead && (
          <LeadViewModal
            lead={viewLead}
            onClose={() => setViewLead(null)}
            onEdit={(lead) => {
              setSelectedLead(lead);
              setEditMode(true);
              setAddLeadActive(true);
            }}
          />
        )}

        {AddLeadActive && (
          <div className="lead-overlay">
            <AddLead
              leadData={editMode ? selectedLead : null}
              onSave={(newBooking) => {
                if (!newBooking) return;
                const primaryService = Array.isArray(newBooking.bookingServices)
                  ? newBooking.bookingServices.find((bs) => bs?.service?.id)
                      ?.service
                  : null;
                const formattedLead = {
                  id: newBooking.id,
                  clientName: newBooking.fullName,
                  contactNumber: newBooking.phone,
                  contactEmail: newBooking.email,
                  address: newBooking.address,
                  postcode: newBooking.postcode || '',
                  label: fromGraphQLStatus(newBooking.status),
                  stage: newBooking.stage,
                  bookingFrequency: newBooking.bookingFrequency,
                  message: newBooking.message,
                  preferredDate: newBooking.preferredDate || '',
                  createdAt: newBooking.createdAt || new Date().toISOString(),
                  source: parseLeadSource(newBooking.bookingFrequency),
                  serviceType: primaryService?.id || '',
                  serviceName: primaryService?.service || '',
                  branchId: newBooking.branchId || '',
                  branchName: newBooking.branchName || newBooking.branch?.name || '',
                  uploads: Array.isArray(newBooking.uploads)
                    ? newBooking.uploads
                    : [],
                };
                setLeads((prev) => {
                  const exists = prev.some((l) => l.id === formattedLead.id);
                  if (exists) {
                    return prev.map((l) =>
                      l.id === formattedLead.id ? { ...l, ...formattedLead } : l,
                    );
                  }
                  return [formattedLead, ...prev];
                });
                setAddLeadActive(false);
                setEditMode(false);
                setSelectedLead(null);
                window.dispatchEvent(new CustomEvent('lead:changed'));
                fetchLeads(1, { background: true });
              }}
              onCancel={() => {
                setAddLeadActive(false);
                setEditMode(false);
                setSelectedLead(null);
              }}
            />
          </div>
        )}

        {confirmDeleteId && (
          <div
            className="lead-overlay"
            onClick={() => !deleting && setConfirmDeleteId(null)}
          >
            <div
              className="confirm-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-delete-title"
            >
              <h3 id="confirm-delete-title" className="confirm-modal__title">
                Delete lead
              </h3>
              <p className="confirm-modal__body">
                Are you sure you want to delete this lead? This action cannot be undone.
              </p>
              <div className="confirm-modal__actions">
                <button
                  type="button"
                  className="action-button"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="action-button action-button--danger"
                  onClick={confirmDeleteLead}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lead;
